import { wrapAngle } from '../core/math';
import type { Ghost } from '../game/session';

/** Ghost positions are stored in 2 cm units — well under a pixel on screen. */
const POS_UNIT = 0.02;
const ANGLE_SCALE = 32768 / Math.PI;

/** Quantisation step for a track of this span: 2 cm, widened only if the
 *  track is too large to fit Int16 at that resolution. */
export function posUnit(span: number): number {
  return Math.max(POS_UNIT, span / 32700);
}

export function bytesToBase64(bytes: Uint8Array): string {
  let out = '';
  const chunk = 0x8000; // spreading the whole array would overflow the stack
  for (let i = 0; i < bytes.length; i += chunk) {
    out += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(out);
}

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Quantise a ghost to Int16. A 35 s lap is ~1050 frames, so this lands around
 * 6 KB rather than the ~30 KB the same data costs as JSON — which matters when
 * localStorage is the only budget available.
 */
/** Speed in cm/s fits any kart speed comfortably inside an Int16. */
const SPEED_UNIT = 0.01;
const STRIDE = 4; // x, y, heading, speed

export function encodeGhost(g: Ghost, originX: number, originY: number, span: number): string {
  const unit = posUnit(span);
  const n = g.x.length;
  const buf = new Int16Array(n * STRIDE);
  for (let i = 0; i < n; i++) {
    buf[i * STRIDE] = Math.round((g.x[i] - originX) / unit);
    buf[i * STRIDE + 1] = Math.round((g.y[i] - originY) / unit);
    buf[i * STRIDE + 2] = Math.round(wrapAngle(g.heading[i]) * ANGLE_SCALE);
    buf[i * STRIDE + 3] = Math.round((g.speed[i] ?? 0) / SPEED_UNIT);
  }
  return bytesToBase64(new Uint8Array(buf.buffer));
}

export function decodeGhost(
  b64: string,
  originX: number,
  originY: number,
  timeMs: number,
  span: number,
): Ghost {
  const unit = posUnit(span);
  const bytes = base64ToBytes(b64);
  const buf = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
  const n = Math.floor(buf.length / STRIDE);
  const x = new Float32Array(n);
  const y = new Float32Array(n);
  const heading = new Float32Array(n);
  const speed = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    x[i] = buf[i * STRIDE] * unit + originX;
    y[i] = buf[i * STRIDE + 1] * unit + originY;
    heading[i] = buf[i * STRIDE + 2] / ANGLE_SCALE;
    speed[i] = buf[i * STRIDE + 3] * SPEED_UNIT;
  }
  // Inputs and telemetry are not persisted with the ghost — they only matter
  // for replaying a run you just drove, and storing them would several-fold the
  // size of every saved lap for data nobody looks at twice.
  return {
    x,
    y,
    heading,
    speed,
    brake: new Float32Array(n),
    steer: new Float32Array(n),
    latG: new Float32Array(n),
    longG: new Float32Array(n),
    frontLoad: new Float32Array(n),
    rearLoad: new Float32Array(n),
    index: new Int32Array(n),
    timeMs,
  };
}
